import type { ElementType } from 'react';
import {
  Box,
  ResponsiveSection,
  type ExtendableBoxProps,
  type ResponsiveValue,
  getResponsiveValue,
  useScreenSize,
} from '@stitch-fix/mode-react';
import classNames from 'classnames';
import styles from './styles.module.scss';

interface Props {
  children: React.ReactNode;
  secondaryContent?: React.ReactNode;
  /**
   * Vertical alignment of the children or children and secondary content.
   *
   * @remarks
   * - `center` vertically centers the children container
   * - `top` vertically stretches the children's container
   * - `center-all` vertically centers the children **and** secondary content on mobile.
   *                Only centers the children on desktop.
   *
   * @default 'center'
   */
  align?: ResponsiveValue<'center' | 'top' | 'center-all'>;
}

export type PanelLayoutProps<C extends ElementType = 'div'> =
  ExtendableBoxProps<C, Props>;

export const PanelLayout = <C extends ElementType = 'div'>({
  children,
  secondaryContent,
  maxWidth = '425px',
  align,
  ...rest
}: PanelLayoutProps<C>) => {
  const screenSize = useScreenSize();
  const responsiveAlign = getResponsiveValue(screenSize, align, 'center');

  return (
    <ResponsiveSection gutter="none">
      <Box<ElementType>
        className={classNames(styles.panel, {
          [styles.top]: responsiveAlign === 'top',
          [styles.centerAll]: responsiveAlign === 'center-all',
        })}
        {...rest}
      >
        <Box className={styles.panelContent} maxWidth={maxWidth}>
          {children}
        </Box>
        {secondaryContent}
      </Box>
    </ResponsiveSection>
  );
};
